import { Badge } from "@/components/ui/baseUi/badge"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { shortenText } from "@/modules/utils/stringUtils"
import { word } from "@modules/core/utils/i18n"
import { toggleInArray } from "@modules/utils/objectUtils"
import { ChevronDownIcon } from "lucide-react"
import { useEffect, useRef, useState, type CSSProperties } from "react"

type Item<T> = {
  value: T
  label: string
  leftSide?: React.ReactNode
}

export type HUMultiSelectProps<T> = {
  values: T[]
  options: Item<T>[]
  emptyMessage?: string
  onChange?: (values: T[]) => void
  valueTemplate?: (item: Item<T>) => React.ReactNode
  className?: string
  style?: CSSProperties
  hideIcon?: boolean
  viewMore?: string
}

export function HUMultiSelect<T>({
  values,
  options,
  emptyMessage,
  onChange,
  valueTemplate,
  className,
  style,
  viewMore,
}: HUMultiSelectProps<T>) {
  const onChangeItem = (item: Item<T>) => {
    const newArray = toggleInArray(values, (value) => value === item.value, item.value)
    onChange?.(newArray as T[])
  }

  const [viewMoreClicked, setViewMoreClicked] = useState<boolean>(false)
  const [hasOverflow, setHasOverflow] = useState<boolean>(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current && !viewMoreClicked) {
      const { scrollHeight, clientHeight } = contentRef.current
      setHasOverflow(scrollHeight > clientHeight)
    }
  }, [options, viewMoreClicked])

  const itemOptionTemplate = (item: Item<T>, index: number) => {
    const isItemSelected = values.includes(item.value)

    if (item.label.length > 24) {
      return (
        <Tooltip key={index}>
          <TooltipTrigger
            render={
              <Badge
                size="lg"
                variant={isItemSelected ? "neutralIntense" : "neutralSubtle"}
                className="h-9 cursor-pointer hover:bg-neutral-200"
                onClick={() => { onChangeItem(item); }}
              >
                {shortenText(item.label, 24)}
              </Badge>
            }
          />

          <TooltipContent side="bottom" className="w-fit max-w-60 wrap-break-word whitespace-normal">
            {item.label}
          </TooltipContent>
        </Tooltip>
      )
    } else
      return (
        <Badge
          key={index}
          size="lg"
          variant={isItemSelected ? "neutralIntense" : "neutralSubtle"}
          className="h-9 cursor-pointer hover:bg-neutral-200"
          onClick={() => { onChangeItem(item); }}
        >
          <Text fontStyle="LM" className="flex items-center text-textDefault-body">
            {item.label}
          </Text>
        </Badge>
      )
  }

  return (
    <div style={style} className={className ?? "flex flex-col items-start gap-2"}>
      {options.length ? (
        <>
          <div
            ref={contentRef}
            className="flex flex-row flex-wrap gap-2"
            style={{
              maxHeight: viewMoreClicked ? "none" : "90px",
              overflow: viewMoreClicked ? "visible" : "hidden",
            }}
          >
            {options.map((option, index) => valueTemplate?.(option) ?? itemOptionTemplate(option, index))}
          </div>

          {hasOverflow && viewMore && !viewMoreClicked && (
            <div
              className="mt-2 flex w-full cursor-pointer flex-col items-center justify-center"
              onClick={() => { setViewMoreClicked(true); }}
              onMouseEnter={(e) => e.currentTarget.children[1]?.classList.add("mt-2")}
              onMouseLeave={(e) => e.currentTarget.children[1]?.classList.remove("mt-2")}
            >
              <Text fontStyle="BS" className="mb-1">
                {viewMore}
              </Text>
              <ChevronDownIcon className="transition-duration-150 size-4 transition-all" />
            </div>
          )}
        </>
      ) : (
        <Text>{emptyMessage ?? word("multiselect.empty.message")}</Text>
      )}
    </div>
  )
}
